import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Check, Zap, MessageSquare, Users, Globe, Shield, Plane } from 'lucide-react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

const FEATURES = [
  { icon: Zap, label: 'Priority visibility in route communities' },
  { icon: MessageSquare, label: 'Exclusive Club members lounge at select airports' },
  { icon: Users, label: 'Advanced layover buddy matching' },
  { icon: Globe, label: 'Unlimited airport guide posts' },
  { icon: Shield, label: 'Verified traveler badge' },
  { icon: Crown, label: 'Fly N Out Club Member badge' },
  { icon: Plane, label: 'Early access to new features' },
  { icon: Check, label: 'Ad-free experience' },
];

export default function Premium() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const handleSubscribe = async () => {
    if (!user) { toast.error('Please log in to subscribe'); return; }
    if (user.is_premium) { toast.info('You are already a Club member!'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { plan: 'monthly', amount: 2900 }, { withCredentials: true });
      setPaymentId(res.data.payment_id);
      toast.info('Payment initiated! (Demo mode - click Simulate Success below)');
    } catch {
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const simulateSuccess = async () => {
    if (!paymentId) return;
    setSimulating(true);
    try {
      await axios.post(`${API}/payments/${paymentId}/simulate-success`, {}, { withCredentials: true });
      await refreshUser();
      toast.success('Welcome to Fly N Out Club!');
    } catch {
      toast.error('Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <PageLayout>
      <SEOHead
        title="Fly N Out Club — Premium Membership SAR 29/month"
        description="Upgrade to Fly N Out Club for exclusive perks: priority seats, premium lounges, advanced buddy matching, a verified badge, and an ad-free experience."
        url="/premium"
        keywords="Fly N Out Club, premium membership, travel subscription, airport community premium, SAR 29"
      />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#111827] via-[#1E3A8A]/80 to-[#111827] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="mb-4 bg-[#FF4500]/20 text-[#FF4500] border-[#FF4500]/30">
            <Crown className="w-3 h-3 mr-1" /> Exclusive Membership
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Fly N Out Club</h1>
          <p className="text-xl text-gray-300 mb-2">The ultimate traveler membership</p>
          <p className="text-gray-400">Join thousands of premium travelers enjoying exclusive benefits</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Pricing card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="fly-card p-6 border-2 border-[#FF4500]/30" data-testid="premium-pricing-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Badge className="bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30 mb-2">Most Popular</Badge>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Monthly Plan</h2>
                </div>
                <Crown className="w-8 h-8 text-[#FF4500]" />
              </div>

              <div className="mb-4">
                <span className="text-4xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>SAR 29</span>
                <span className="text-muted-foreground">/month</span>
              </div>

              <p className="text-sm text-muted-foreground mb-6">Cancel anytime. No hidden fees. Secure payment via Moyasar.</p>

              {user?.is_premium ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#10B981]/15 text-[#10B981] font-medium">
                    <Check className="w-4 h-4" /> You are a Club Member!
                  </div>
                  {user.premium_until && (
                    <p className="text-xs text-center text-muted-foreground">
                      Active until {new Date(user.premium_until).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Button className="fly-btn-primary w-full py-6 text-base" onClick={handleSubscribe} disabled={loading} data-testid="subscribe-btn">
                    <Crown className="mr-2 w-5 h-5" />
                    {loading ? 'Processing...' : 'Subscribe - SAR 29/month'}
                  </Button>
                  {paymentId && (
                    <Button variant="outline" className="w-full" onClick={simulateSuccess} disabled={simulating} data-testid="simulate-payment-btn">
                      {simulating ? 'Activating...' : 'Simulate Payment Success (Demo)'}
                    </Button>
                  )}
                  <p className="text-xs text-center text-muted-foreground">Demo mode: Moyasar integration ready. Real keys coming soon.</p>
                </div>
              )}

              <div className="border-t border-border mt-6 pt-4">
                <p className="text-xs text-muted-foreground text-center">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Secure payment via Moyasar • SAR 29 charged monthly
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Features */}
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Everything in Club</h3>
            <div className="space-y-3">
              {FEATURES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#FF4500]" />
                  </div>
                  <span className="text-sm">{label}</span>
                </div>
              ))}
            </div>
            <Card className="fly-card p-4 mt-6 bg-muted/50">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Payment Methods:</strong> Visa, Mastercard, MADA, Apple Pay, STC Pay — all via Moyasar.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
